"use client";

import React, { useState } from 'react';
import { Plus, Save, Loader2 } from 'lucide-react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { TimelineItem } from './TimelineItem';
import { Segment } from '@/types/segment.interface';
import { Group } from '@/types/group.interface';
import Api from '@/lib/api';
import { useModal } from '@/redux/useModal';

interface TimelineListProps {
    segments: Segment[];
    setSegments: React.Dispatch<React.SetStateAction<Segment[]>>;
    currentGroup: Group | null;
    setIsSegmentsDirty: (dirty: boolean) => void;
    modaler: ReturnType<typeof useModal>;
    openPreview: (segment: Segment) => void;
    uploadingSegmentId: number | null;
    setUploadingSegmentId: (id: number | null) => void;
}

export const TimelineList: React.FC<TimelineListProps> = ({
    segments,
    setSegments,
    currentGroup,
    setIsSegmentsDirty,
    modaler,
    openPreview,
    uploadingSegmentId,
    setUploadingSegmentId,
}) => {
    const [isSaving, setIsSaving] = useState(false);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleSegmentDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = segments.findIndex((s) => s.id === active.id);
        const newIndex = segments.findIndex((s) => s.id === over.id);
        const newSegments = arrayMove(segments, oldIndex, newIndex).map((s, i) => ({ ...s, order: i }));
        setSegments(newSegments);
        setIsSegmentsDirty(true);
    };

    const addSegment = () => {
        if (!currentGroup) return;
        const newSegment: Segment = {
            id: -(Date.now()), // Temporary negative ID
            order: segments.length,
            desc: `镜头 ${segments.length + 1}`,
            prompt: "",
            negativePrompt: "",
            status: 'pending',
            groupId: currentGroup.id,
            referenceImages: []
        };
        setSegments([...segments, newSegment]);
        setIsSegmentsDirty(true);
    };

    const saveSegments = async () => {
        if (!currentGroup) return;
        setIsSaving(true);
        try {
            const res = await Api.segments.sync(currentGroup.id, segments);
            setSegments(res);
            setIsSegmentsDirty(false);
        } catch (error) {
            console.error("Save segments error:", error);
            alert("保存失败，请重试");
        } finally {
            setIsSaving(false);
        }
    };

    const deleteSegment = async (id: number) => {
        if (!await modaler.confirm("确定删除此片段吗？")) return;
        if (id < 0) {
            setSegments(segments.filter(s => s.id !== id));
            setIsSegmentsDirty(true);
            return;
        }
        try {
            await Api.segments.delete(id);
            setSegments(segments.filter(s => s.id !== id));
        } catch (error) { console.error("Delete segment error:", error); }
    };

    const handleSegmentImageUpload = async (segmentId: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !currentGroup) return;
        setUploadingSegmentId(segmentId);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('segmentId', segmentId.toString());
            formData.append('groupId', currentGroup.id);
            const res = await fetch('/api/upload/reference-image', { method: 'POST', body: formData });
            if (res.ok) {
                const refreshed = await Api.groups.get(currentGroup.id);
                if (refreshed.segments) setSegments(refreshed.segments);
            }
        } catch (error) { console.error('Upload error:', error); }
        finally { setUploadingSegmentId(null); }
    };

    const handleSegmentImageDelete = async (imageId: number) => {
        if (!await modaler.confirm('确定删除此参考图吗？')) return;
        try {
            await Api.referenceImages.delete(imageId);
            if (currentGroup) {
                const refreshed = await Api.groups.get(currentGroup.id);
                if (refreshed.segments) setSegments(refreshed.segments);
            }
        } catch (error) { console.error('Delete image error:', error); }
    };

    const clearSegmentVideo = async (segmentId: number) => {
        if (!await modaler.confirm("确定清除此片段的生成结果吗？")) return;
        try {
            await Api.segments.clearVideo(segmentId);
            setSegments(prev => prev.map(s => s.id === segmentId ? { ...s, videoUrl: null, videoKey: null, status: 'pending' } : s));
        } catch (error) { console.error("Clear video error:", error); }
    };

    const handleDownload = async (key: string, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        try {
            const response = await fetch(`/api/download?key=${encodeURIComponent(key)}`);
            if (!response.ok) throw new Error('Download failed');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = key.split('/').pop() || 'video.mp4';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Download error:', error);
            alert('文件下载失败');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-3">
                    <h2 className="text-xs font-black uppercase tracking-[0.2em] text-zinc-400 dark:text-zinc-500">剧本时间轴 (Timeline)</h2>
                    <span className="px-2 py-0.5 bg-zinc-100 dark:bg-zinc-800 text-[10px] font-bold text-zinc-500 rounded-none border border-zinc-200 dark:border-zinc-700">
                        {segments.length} 片段
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={addSegment}
                        disabled={segments.length >= 10}
                        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-500/30 transition-all text-[10px] font-black uppercase tracking-widest disabled:opacity-20 disabled:cursor-not-allowed group"
                    >
                        <Plus size={14} className="group-hover:rotate-90 transition-transform" />
                        添加片段
                    </button>
                    <button
                        onClick={saveSegments}
                        disabled={isSaving || segments.length === 0}
                        className={`flex items-center gap-2 px-4 py-2 font-black text-[10px] uppercase tracking-widest transition-all ${isSaving || segments.length === 0 ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-600/20"}`}
                    >
                        {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        保存所有
                    </button>
                </div>
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSegmentDragEnd}>
                <SortableContext items={segments.map(s => s.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1">
                        {segments.map((segment, index) => (
                            <TimelineItem
                                key={segment.id}
                                index={index}
                                segment={segment}
                                isLast={index === segments.length - 1}
                                onUpdate={(id, updates) => {
                                    setSegments(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
                                    setIsSegmentsDirty(true);
                                }}
                                onDelete={deleteSegment}
                                onDownload={handleDownload}
                                onPreview={openPreview}
                                onUploadImage={handleSegmentImageUpload}
                                onDeleteImage={handleSegmentImageDelete}
                                onDeleteVideo={clearSegmentVideo}
                                isUploadingImage={uploadingSegmentId === segment.id}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>

            {segments.length === 0 && (
                <div className="py-20 text-center border-2 border-dashed border-zinc-100 dark:border-zinc-800/50 rounded-none bg-zinc-50/30 dark:bg-transparent">
                    <div className="max-w-xs mx-auto space-y-4">
                        <div className="w-12 h-12 bg-zinc-100 dark:bg-zinc-800 rounded-none flex items-center justify-center mx-auto mb-4">
                            <Plus size={24} className="text-zinc-300" />
                        </div>
                        <h3 className="text-sm font-bold text-zinc-800 dark:text-zinc-200 uppercase tracking-widest">开启你的创作</h3>
                        <p className="text-[10px] text-zinc-500 uppercase font-bold leading-relaxed">点击右上角 “添加片段” 或使用 AI 助理一键生成爆款脚本</p>
                    </div>
                </div>
            )}
        </div>
    );
};
